import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, Plus, AlertTriangle, ArrowUpRight, ArrowDownRight, Clock, Building2, Smartphone } from 'lucide-react';
import { TopUpDialog } from './TopUpDialog';
import { useWallet } from '@/hooks/useWallet';

type Method = 'bank_transfer' | 'telebirr' | 'santimpay' | 'talk_to_sales';

function generateReference(method: Method) {
  const prefix = method === 'telebirr' ? 'TB' : method === 'bank_transfer' ? 'BT' : method === 'santimpay' ? 'SP' : 'WTU';
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

export function WalletCard() {
  const { user } = useAuth();
  const { balance, lowBalance, isLoading } = useWallet();
  const [open, setOpen] = useState(false);
  const [prefillAmount, setPrefillAmount] = useState<number | undefined>();
  const [prefillMethod, setPrefillMethod] = useState<Method | undefined>();
  const [prefillReference, setPrefillReference] = useState<string | undefined>();
  const [prefillPhone, setPrefillPhone] = useState<string | undefined>();
  const [quickMethod, setQuickMethod] = useState<'telebirr' | 'bank_transfer'>('telebirr');

  // Fetch profile phone once for prefill
  const { data: profile } = useQuery({
    queryKey: ['profile-phone', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('phone')
        .eq('id', user!.id)
        .maybeSingle();
      return data as { phone?: string | null } | null;
    },
  });

  const quickTopUp = (amount: number, method: Method) => {
    setPrefillAmount(amount);
    setPrefillMethod(method);
    setPrefillReference(generateReference(method));
    setPrefillPhone(profile?.phone || undefined);
    setOpen(true);
  };

  const { data: txs } = useQuery({
    queryKey: ['wallet-txs-recent', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_transactions' as any)
        .select('id, direction, amount_etb, kind, note, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(3);
      return (data as any[]) || [];
    },
  });

  const { data: pending } = useQuery({
    queryKey: ['wallet-topup-pending', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_topup_requests' as any)
        .select('id, amount_etb, status')
        .eq('user_id', user!.id)
        .in('status', ['pending', 'awaiting_verification']);
      return (data as any[]) || [];
    },
  });

  const pendingTotal = (pending || []).reduce((s, r) => s + Number(r.amount_etb || 0), 0);

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 h-32 animate-pulse bg-muted/40" />
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-br from-emerald-500/[0.06] to-transparent border border-emerald-500/10">
        <CardContent className="p-4 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="rounded-lg bg-emerald-500/10 p-1.5 shrink-0">
                <Wallet className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Wallet Balance</p>
                <p className="text-[11px] text-muted-foreground">Spend on packages, credits & boosts</p>
              </div>
            </div>
            {lowBalance && (
              <Badge variant="destructive" className="gap-1 text-[10px] shrink-0">
                <AlertTriangle className="h-3 w-3" /> Low
              </Badge>
            )}
          </div>

          {/* Balance */}
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums">{balance.toLocaleString()}</span>
            <span className="text-sm text-muted-foreground">ETB</span>
          </div>

          {pendingTotal > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-600">
              <Clock className="h-3 w-3" />
              {pendingTotal.toLocaleString()} ETB pending verification ({pending!.length} request{pending!.length > 1 ? 's' : ''})
            </div>
          )}

          {/* Recent transactions */}
          {txs && txs.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {t.direction === 'credit'
                      ? <ArrowDownRight className="h-3 w-3 text-emerald-600 shrink-0" />
                      : <ArrowUpRight className="h-3 w-3 text-destructive shrink-0" />}
                    <span className="truncate text-muted-foreground">
                      {t.note || t.kind.replace('_', ' ')}
                    </span>
                  </div>
                  <span className={`font-medium tabular-nums shrink-0 ${t.direction === 'credit' ? 'text-emerald-600' : 'text-destructive'}`}>
                    {t.direction === 'credit' ? '+' : '-'}{Number(t.amount_etb).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Quick top-up: method selector + amount chips */}
          <div className="pt-1 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Quick top up ETB</p>
              <div className="inline-flex rounded-md border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setQuickMethod('telebirr')}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-sm transition-colors ${quickMethod === 'telebirr' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Smartphone className="h-3 w-3" /> Telebirr
                </button>
                <button
                  type="button"
                  onClick={() => setQuickMethod('bank_transfer')}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-sm transition-colors ${quickMethod === 'bank_transfer' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Building2 className="h-3 w-3" /> Bank
                </button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[1000, 5000, 10000, 25000].map((amt) => (
                <Button
                  key={amt}
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px] gap-1"
                  onClick={() => quickTopUp(amt, quickMethod)}
                >
                  <Plus className="h-3 w-3" />
                  {amt.toLocaleString()}
                </Button>
              ))}
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button asChild size="sm" variant="ghost" className="h-7 text-xs gap-1">
              <Link to="/dashboard/wallet">View all <ArrowUpRight className="h-3 w-3" /></Link>
            </Button>
            <Button size="sm" className="h-7 gap-1" onClick={() => quickTopUp(5000, quickMethod)}>
              <Plus className="h-3.5 w-3.5" /> Custom Top Up
            </Button>
          </div>
        </CardContent>
      </Card>
      <TopUpDialog
        open={open}
        onOpenChange={setOpen}
        defaultAmount={prefillAmount}
        defaultMethod={prefillMethod}
        defaultReference={prefillReference}
        defaultContactPhone={prefillPhone}
      />
    </>
  );
}
